import React, { useCallback, useState } from 'react';
import { INotification, IBookingEventPayload, isBookingEventPayload, isBasketEventPayload } from 'services/CrmApi/types/NotificationTypes';
import { DateWithTime } from 'ui/DateWithTime/DateWithTime';
import { NotificationTypeTag } from './NotificationTypeTag';
import { formatPriceWithCurrencySymbol } from 'utils';
import { CrmActivityPerson } from 'containers/CRM/crmActivity/icons/CrmActivityPerson.component';
import { NotificationCurrencySign } from 'ui/Icons/components/NotificationCurrencySign.component';
import { generateBookingLink } from 'utils/bookingManager';
import { Link } from 'ui/Link';
import { DoubleCheckIcon } from 'ui/Icons/components/DoubleCheck.component';
import { theme } from '../../../tailwind.config';
import { useNotificationMarkRead } from 'services/CrmApi/mutations/useNotificationMarkRead';
import { useNotifications } from 'hooks/useNotifications';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import { useQueryClient } from '@tanstack/react-query';
import { TCurrencyCode } from 'interfaces';

interface INotificationItemProps {
  notification: INotification;
  notifyUpdateStatus: (isUpdating: boolean) => void;
  notifyReadStatus: (notificationId: string, read: boolean) => void;
}

export const NotificationItem: React.FC<INotificationItemProps> = ({ notification, notifyReadStatus, notifyUpdateStatus }) => {
  const { showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();
  const relatedContact = notification.referencedDirectoryEntries?.find(entry => !!entry.contactType);
  const relatedCompany = notification.referencedDirectoryEntries?.find(entry => !!entry.companyType);
  let nameRow = `${relatedContact?.fullName}`;
  if (relatedCompany) {
    nameRow = `${relatedContact?.fullName} (${relatedCompany?.fullName})`;
  }

  let bookingCurrencyCode: TCurrencyCode | null = null;
  if (isBookingEventPayload(notification.event.payload)) {
    bookingCurrencyCode = notification.event.payload.bookingCurrencyCode;
  }
  if (isBasketEventPayload(notification.event.payload)) {
    bookingCurrencyCode = notification.event.payload.basketCurrencyCode;
  }
  let bookingSalesCostCents: number | null = null;
  if (isBookingEventPayload(notification.event.payload)) {
    bookingSalesCostCents = notification.event.payload.bookingSalesCostCents;
  }
  if (isBasketEventPayload(notification.event.payload)) {
    bookingSalesCostCents = notification.event.payload.basketItemTotalAmountCents;
  }

  const notificationMarkReadMutation = useNotificationMarkRead();
  const markNotificationAsReadOrUnread = useCallback(async () => {
    try {
      notifyUpdateStatus(true);
      notifyReadStatus(notification.id, !notification.read);
      await notificationMarkReadMutation.mutateAsync({
        notificationId: notification.id,
        read: !notification.read,
      });
      queryClient.invalidateQueries({ queryKey: ['crm-notifications'] });
      notifyUpdateStatus(false);
    } catch (error) {
      notifyUpdateStatus(false);
      notifyReadStatus(notification.id, notification.read);
      showErrorNotification('Failed to update notification status.');
    }
  }, [notificationMarkReadMutation, notification.id, notification.read, notifyReadStatus]);

  return (
    <div className="notification-item flex flex-col gap-[5px] p-[10px] border border-solid border-gray-20 rounded-[4px]">
      <div className="notification-type-date-row flex items-center justify-between">
        <NotificationTypeTag type={notification.event.type} />
        <DateWithTime date={notification.event.date} />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[5px]">
          <div className="min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px] flex items-center justify-center">
            <CrmActivityPerson />
          </div>
          <p className="font-pt-sans text-[13px] text-black tracking-[0.52px] m-0">
            {nameRow}
          </p>
        </div>
        <HidingTextTooltip
          tooltipContentClassname="px-[8px] py-[5px]"
          tooltipContent={notification.read ? 'Mark as unread' : 'Mark as read'}
          position="bottom"
        >
          <div className="flex items-center justify-center cursor-pointer" onClick={markNotificationAsReadOrUnread}>
            <DoubleCheckIcon fill={notification.read ? theme.colors['green-message'] : theme.colors['light-gray']} />
          </div>
        </HidingTextTooltip>
      </div>
      {/* the following should be shown only for Booking event notifications */}
      {isBookingEventPayload(notification.event.payload) ? (
        <div className="flex items-center gap-[5px]">
          <span className="flex items-center justify-center font-pt-sans text-[17px] text-gray-60 min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px]">#</span>
          <Link to={generateBookingLink(notification.event.payload.bookingId)} className="font-pt-sans text-[13px] text-black tracking-[0.52px] m-0 underline hover:no-underline">
            {notification.event.payload.bookingHumanReadableId}
          </Link>
        </div>
      ) : null}
      {/* the following should be shown only for Basket event notifications */}
      {isBasketEventPayload(notification.event.payload) ? (
        <div className="flex items-center gap-[5px]">
          <span className="flex items-center justify-center font-pt-sans text-[17px] text-gray-60 min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px]">#</span>
          <span className="font-pt-sans text-[13px] text-black tracking-[0.52px] m-0">
            {notification.event.payload.basketHumanReadableId}
          </span>
        </div>
      ) : null}
      {/* the following should be shown only for Booking and Basket event notifications */}
      {(isBookingEventPayload(notification.event.payload) || isBasketEventPayload(notification.event.payload)) ? (
        <div className="flex items-center gap-[5px]">
          <NotificationCurrencySign />
          <p className="font-pt-sans text-[13px] text-green-message tracking-[0.52px] m-0">
            {formatPriceWithCurrencySymbol(bookingCurrencyCode, bookingSalesCostCents)}
          </p>
        </div>
      ) : null}
    </div>
  );
};
